import { useQuery } from "@tanstack/react-query";
import {
  Bolt,
  CheckCircle2,
  CircleDashed,
  CircleX,
  Folder,
  Tags,
} from "lucide-react";
import type { ReactNode } from "react";

import {
  FLOW_ACTION_LABELS,
  FLOW_ACTION_STATUS_LABELS,
  FLOW_FIELD_LABELS,
  FLOW_FORMAT_LABELS,
  FLOW_OPERATOR_LABELS,
  FLOW_RUN_STATUS_LABELS,
  formatFlowTagName,
  getFlowActionResultTarget,
  type FlowAction,
  type FlowRun,
} from "@/modules/flow";
import { getFolderNameById, mineApi, mineQueryKeys, useSidebarData } from "@/modules/mine";
import type { SheetComponentProps } from "@/shared/components/sheet";
import { formatFullDateTime, formatRelativeTime } from "@/shared/lib/date";
import { cn } from "@/shared/lib/utils";
import { SheetDescription, SheetHeader, SheetTitle } from "@/shared/ui/sheet";

interface FlowRunDetailSheetProps extends SheetComponentProps {
  itemTitle?: string;
  run: FlowRun;
}

type StepStatus = "success" | "error" | "muted";

const STEP_STATUS_STYLES: Record<StepStatus, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400",
  error: "border-destructive/25 bg-destructive/5 text-destructive",
  muted: "border-border bg-muted/40 text-muted-foreground",
};

function ItemTitle({ fallback, itemId }: { fallback?: string; itemId: string }) {
  const query = useQuery({
    queryKey: mineQueryKeys.itemDetail(itemId),
    queryFn: () => mineApi.getItem(itemId, { silent: true }),
    enabled: !fallback,
    retry: false,
  });

  return <>{fallback || query.data?.title || "未命名收藏"}</>;
}

function StepStatusIcon({ status }: { status: StepStatus }) {
  const Icon = status === "success"
    ? CheckCircle2
    : status === "error" ? CircleX : CircleDashed;

  return <Icon className="size-4" />;
}

function FlowConnector({ status = "success" }: { status?: StepStatus }) {
  return (
    <div className="ml-[19px] flex h-5 items-center">
      <span className={cn(
        "h-full border-l",
        status === "success" && "border-emerald-300 dark:border-emerald-800",
        status === "error" && "border-destructive/40",
        status === "muted" && "border-dashed border-border",
      )} />
    </div>
  );
}

function FlowStep({
  icon,
  status,
  summary,
  title,
}: {
  icon?: ReactNode;
  status: StepStatus;
  summary: ReactNode;
  title: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-background px-3 py-3">
      <span className={cn("flex size-8 shrink-0 items-center justify-center rounded-full border", STEP_STATUS_STYLES[status])}>
        {icon ?? <StepStatusIcon status={status} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{summary}</span>
      </span>
    </div>
  );
}

function formatConditionValue(field: string, value?: string) {
  if (!value) return "—";
  return field === "format" ? FLOW_FORMAT_LABELS[value] ?? value : value;
}

function ConditionSummary({ run }: { run: FlowRun }) {
  return (
    <span className="block space-y-2">
      {run.condition_result.map((condition, index) => {
        const expectedValues = condition.expected_values?.length
          ? condition.expected_values
          : condition.expected ? [condition.expected] : [];
        const expected = expectedValues
          .map((value) => formatConditionValue(condition.field, value))
          .join("、") || "—";

        return (
          <span className="block" key={`${condition.field}-${index}`}>
            <span className="block">
              {FLOW_FIELD_LABELS[condition.field as keyof typeof FLOW_FIELD_LABELS] ?? condition.field}
              <span className="mx-1.5">{FLOW_OPERATOR_LABELS[condition.operator as keyof typeof FLOW_OPERATOR_LABELS] ?? condition.operator}</span>
              {expected}
            </span>
            <span className="mt-0.5 block break-all text-muted-foreground/80">
              实际值：{formatConditionValue(condition.field, condition.actual)}
            </span>
            {condition.message ? (
              <span className="mt-0.5 block text-muted-foreground/80">{condition.message}</span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

function getActionLabel(type: string) {
  return FLOW_ACTION_LABELS[type as keyof typeof FLOW_ACTION_LABELS] ?? type;
}

function getConfiguredActionTarget(
  action: FlowAction,
  folders: ReturnType<typeof useSidebarData>["folders"],
  tags: ReturnType<typeof useSidebarData>["tags"],
) {
  if (action.type === "move_to_folder") {
    return getFolderNameById(action.folder_id, folders);
  }

  const tagMap = new Map(tags.map((tag) => [String(tag.id), tag.name]));
  return action.tag_ids.map((id) => formatFlowTagName(tagMap.get(String(id)) ?? id)).join("、");
}

export function FlowRunDetailSheet({ itemTitle, run }: FlowRunDetailSheetProps) {
  const runFailed = run.status === "failed" || run.status === "partially_failed";

  return (
    <>
      <SheetHeader className="border-b px-6 py-5">
        <SheetTitle className="truncate pr-8 text-base font-semibold tracking-tight">
          <ItemTitle fallback={itemTitle} itemId={run.item_id} />
        </SheetTitle>
        <SheetDescription className="sr-only">
          {FLOW_RUN_STATUS_LABELS[run.status]}，{formatRelativeTime(run.created_at) || "未知时间"}
        </SheetDescription>
      </SheetHeader>

      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <div className="mb-5 flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2.5 text-xs">
          <span className={cn("inline-flex items-center gap-1", runFailed ? "text-destructive" : "text-emerald-600 dark:text-emerald-400")}>
            <StepStatusIcon status={runFailed ? "error" : "success"} />
            {FLOW_RUN_STATUS_LABELS[run.status]}
          </span>
          <span className="text-muted-foreground" title={formatFullDateTime(run.created_at)}>
            {formatRelativeTime(run.created_at) || "未知时间"}
          </span>
        </div>
        <FlowRunPath run={run} />
      </div>
    </>
  );
}

export function FlowRunPath({ run, showTitle = true }: { run: FlowRun; showTitle?: boolean }) {
  const { folders, tags } = useSidebarData();
  const conditionLogic = run.rule_version.condition_logic;
  const conditionsMatched = run.condition_result.length === 0 || (
    conditionLogic === "any"
      ? run.condition_result.some((condition) => condition.matched)
      : run.condition_result.every((condition) => condition.matched)
  );

  return (
    <>
      {run.error_message ? (
        <div className="mb-5 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {run.error_message}
        </div>
      ) : null}

      <section>
        {showTitle ? <h2 className="mb-3 text-sm font-medium">执行路径</h2> : null}

          <FlowStep
            icon={<Bolt className="size-4" />}
            status="success"
            summary="系统在收藏信息准备好后开始执行"
            title="新收藏创建后"
          />

          <FlowConnector status={conditionsMatched ? "success" : "error"} />

          <FlowStep
            status={conditionsMatched ? "success" : "error"}
            summary={<ConditionSummary run={run} />}
            title={conditionsMatched ? "条件匹配" : "条件未匹配"}
          />

          {run.action_results.length ? run.action_results.map((action) => {
            const failed = action.status === "failed";
            const muted = action.status === "skipped_already_done" || action.status === "stopped";
            const status: StepStatus = failed ? "error" : muted ? "muted" : "success";
            const targets = getFlowActionResultTarget(action);
            const summary = targets.join("、") || FLOW_ACTION_STATUS_LABELS[action.status];

            return (
              <div key={`${action.index}-${action.type}`}>
                <FlowConnector status={status} />
                <FlowStep
                  icon={action.type === "move_to_folder" ? <Folder className="size-4" /> : <Tags className="size-4" />}
                  status={status}
                  summary={failed ? action.message || action.error_code || summary : summary}
                  title={failed ? `${getActionLabel(action.type)}失败` : getActionLabel(action.type)}
                />
              </div>
            );
          }) : run.rule_version.actions.map((action, index) => (
            <div key={`${action.type}-${index}`}>
              <FlowConnector status="muted" />
              <FlowStep
                icon={action.type === "move_to_folder" ? <Folder className="size-4" /> : <Tags className="size-4" />}
                status="muted"
                summary={getConfiguredActionTarget(action, folders, tags) || "未执行"}
                title={`${getActionLabel(action.type)} · 未执行`}
              />
            </div>
          ))}
      </section>

      {run.stopped_following_rules ? (
        <p className="mt-5 text-xs text-muted-foreground">此流程命中后已停止执行后续流程。</p>
      ) : null}
    </>
  );
}
